import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { THAILAND_COUNTRY_ID } from "mt-shared/location";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiInfoCircle } from "react-icons/bi";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useAnalyticsTracker } from "../../../components/Atoms/Analytics";
import { isSumoPracticeProductId } from "../../../constants/products";
import { MAX_BOOKING_MONTHS_IN_ADVANCE } from "../../../core/availability";
import {
  Coupon,
  Currency,
  Iso639,
  Product,
  ProductAvailabilityType,
} from "../../../graphql/operations";
import useAvailability from "../../../hooks/useAvailability";
import {
  getMaxPrivateGroup,
  getMinPrivateGroup,
  getPrivateGroupPrice,
  getPrivateGroupSize,
  hasPrivateGroups,
} from "../../../utils/get-private-group";
import Input from "../../Atoms/Input";
import DietarySummaryCard from "../../Cards/DietarySummaryCard/DietarySummaryCard";
import ReservationDatepicker from "../../Cards/ReservationDatepicker";
import CheckoutFooter from "../../Layout/CheckoutFooter";
import FAQsSection from "../Product/Parts/FAQsSection";
import Inclusions from "../Product/Parts/Inclusions";
import ProductWaitingListButton from "../Product/Parts/ProductWaitingListButton";
import styles from "./Checkout.module.scss";
import CancellationPolicyIncentive from "./Parts/CancellationPolicyIncentive";
import { CheckoutSummary } from "./Parts/CheckoutSummary";
import AlternateCheckoutSummary from "./Parts/CheckoutSummary/AlternateSummary";
import CollapsibleFieldset from "./Parts/CollapsibleFieldset/CollapsibleFieldset";
import ContactPicker from "./Parts/ContactPicker";
import CouponCodePicker from "./Parts/CouponCodePicker";
import { GroupPriceDetails } from "./Parts/GroupPriceDetails";
import GuestPicker from "./Parts/GuestPicker";
import GuidePickerController from "./Parts/GuidePickerController";
import HowThisWorks from "./Parts/HowThisWorks";
import MarketingSubscriptionCheckbox from "./Parts/MarketingSubscriptionCheckbox";
import ProductPreview from "./Parts/ProductPreview";
import ReservationOffer from "./Parts/ReservationOffer";
import SecurePaymentsFooter from "./Parts/SecurePaymentsFooter";
import CheckoutSubmitButton from "./Parts/SubmitButton";
import {
  FormStateInput,
  validateFormInput,
} from "./Parts/SubmitButton/validation";
const PickupLocation = dynamic(() => import("./Parts/PickupLocation"), {
  ssr: false,
});

function detailsToQuantity(
  isPrivate: boolean,
  details: { quantity: number; excludeFromPrivateGroupCount: boolean }[]
) {
  return details
    .filter((detail) => !isPrivate || !detail.excludeFromPrivateGroupCount)
    .reduce((acc, detail) => acc + detail.quantity, 0);
}

const defaultState: State = {
  names: [],
  email: null,
  confirmEmail: null,
  contactNumber: null,
  campaignSource: null,
  reservationDate: null,
  reservationTime: null,
  specialRequests: undefined,
  couponCode: undefined,
  couponCodeIsValid: false,
  hasEarlyBirdCoupon: false,
  details: [],
  acceptsMarketing: false,
  isPrivate: false,
  groupId: undefined,
  guideId: undefined,
  coupon: null,
};

export type State = FormStateInput & {
  campaignSource: string | null;
  couponCodeIsValid: boolean;
  hasEarlyBirdCoupon: boolean;
  groupId: string | undefined;
  guideId: string | undefined;
  coupon: Coupon | null;
};

type CheckoutProps = {
  initialState: FormStateInput;
  availability: any[];
  product: Product;
  currency: Currency;
  language: Iso639;
  countryCode: string;
  isPrivate?: boolean;
};

export type pickupLocationType = {
  name: string;
  address: string;
  query: {
    placeId: string;
    geoLocation: { lat: number; lng: number };
  };
} | null;

export enum PickupAnswer {
  IDK = "idk",
  DEFAULT_MEETING_PLACE = "defaultMeetingPlace",
  IKNOW = "iknow",
}

export default function Checkout({
  initialState,
  product,
  availability,
  currency,
  language,
  countryCode,
  ...props
}: CheckoutProps) {
  const tracker = useAnalyticsTracker();
  const [inclusionModalOpen, setInclusionModalOpen] = useState(0);
  const [isPrivate, setIsPrivate] = useState(props.isPrivate);

  const [pickupLocation, setPickupLocation] =
    useState<pickupLocationType>(undefined);
  const [pickUpAnswer, setPickUpAnswer] = useState(PickupAnswer.IDK);

  const [openFaq, setOpenFaq] = useState(false);
  const [openDis, setOpenDis] = useState(false);

  const onOpenDisModal = () => setOpenDis(true);
  const onCloseDisModal = () => setOpenDis(false);

  const onOpenFaqModal = () => setOpenFaq(true);
  const onCloseFaqModal = () => setOpenFaq(false);

  const { location } = product;

  const requireAllGuestNames = location.country.id === THAILAND_COUNTRY_ID;

  const modalRef = useRef(null);

  const [state, setState] = useState<State>({
    ...defaultState,
    ...initialState,
  });

  const trackField = useCallback(
    (kv: { [key: string]: any } = {}) => {
      const key = Object.keys(kv)[0];
      const eventValue = kv[key] || "";
      const eventName = `checkout::${key}::${eventValue}`;
      const blacklist = ["totalAmount", "names", "email"];

      if (!blacklist.includes(key)) {
        tracker.trackEvent({
          category: "onpage",
          action: "checkout",
          label: eventName,
        });
      }
    },
    [tracker]
  );

  const handleVariantChange =
    (maxNames?: number) =>
    ({
      typeId,
      quantity,
      customerType,
      price,
      currency,
      excludeFromPrivateGroupCount,
    }: {
      typeId: string;
      quantity: number;
      customerType: string;
      price: number;
      currency: string;
      excludeFromPrivateGroupCount: boolean;
    }) => {
      const { details } = state;

      const newDetailsState = details.map((d) => {
        return d.typeId === typeId
          ? {
              ...d,
              typeId,
              quantity,
              customerType,
              price,
              currency,
              excludeFromPrivateGroupCount,
            }
          : d;
      });

      const totalGuests = newDetailsState.reduce(
        (sum, { quantity }) => sum + quantity,
        0
      );

      const totalNames = maxNames ?? totalGuests;

      const names = state.names.slice(0, maxNames ?? totalGuests).concat(
        Array.from(
          {
            length: Math.max(totalNames - state.names.length, 0),
          },
          () => ({
            givenName: "",
            familyName: "",
          })
        )
      );

      setState({
        ...state,
        names,
        details: newDetailsState,
      });
      trackField({ [typeId]: quantity });
    };

  const handleReservationDateChange = useCallback((date: Date | null) => {
    setState((state) => ({
      ...state,
      hasEarlyBirdCoupon: false,
      couponCodeIsValid: false,
      couponCode: null,
      reservationDate: date,
      guideId: undefined,
    }));
  }, []);

  const handleReservationTimeChange = useCallback((time: string | null) => {
    setState((state) => ({
      ...state,
      hasEarlyBirdCoupon: false,
      couponCodeIsValid: false,
      couponCode: null,
      reservationTime: time,
      guideId: undefined,
    }));
  }, []);

  const updateForm = useCallback(
    (
      args: Partial<State> | ((prevState: State) => State),
      skipTracking?: boolean
    ) => {
      setState((state) => {
        const nextState =
          typeof args === "function" ? args(state) : { ...state, ...args };
        !skipTracking && trackField(args);
        return nextState;
      });
    },
    [trackField]
  );

  const handleGuidePick = (guideId: string) => {
    setState({
      ...state,
      guideId: guideId,
    });
    trackField({ guideId: guideId });
  };

  const handleMarketingSubscriptionCheckboxChange = (checked: boolean) => {
    setState({
      ...state,
      acceptsMarketing: checked,
    });
  };

  const updateCouponCode = (coupon: Coupon) => {
    const couponCode = coupon ? coupon.code : null;
    const couponCodeIsValid = coupon.code !== null ? true : false;

    setState({
      ...state,
      hasEarlyBirdCoupon: false,
      couponCodeIsValid,
      couponCode,
      coupon,
    });
  };

  const groupSize = useMemo(
    () => detailsToQuantity(isPrivate, state.details),
    [state.details, isPrivate]
  );

  const canToggleGroupPrivacy = useMemo(
    () =>
      product.availabilityType === ProductAvailabilityType.PublicAndPrivate &&
      hasPrivateGroups(product),
    [product]
  );

  const minPrivateGroupSize = useMemo(
    () => getMinPrivateGroup(product),
    [product]
  );

  const maxPrivateGroupSize = useMemo(
    () => getMaxPrivateGroup(product),
    [product]
  );

  const numPayingCustomers = useMemo(
    () => getPrivateGroupSize(state.details),
    [state.details]
  );

  const privatePrice = useMemo(
    () => getPrivateGroupPrice(product, numPayingCustomers, isPrivate),
    [product, isPrivate, numPayingCustomers]
  );

  const validateForm = useCallback(() => {
    return validateFormInput({
      details: state.details,
      reservationDate: state.reservationDate,
      reservationTime: state.reservationTime,
      names: state.names,
      email: state.email,
      confirmEmail: state.confirmEmail,
      contactNumber: state.contactNumber,
      acceptsMarketing: state.acceptsMarketing,
      specialRequests: state.specialRequests,
      couponCode: state.couponCode,
      isPrivate: isPrivate,
      maxPrivateGroupSize,
      minPrivateGroupSize,
      guideId: state.guideId,
      pickupAnswer: pickUpAnswer,
      pickupLocation: pickupLocation,
    });
  }, [
    state.acceptsMarketing,
    state.contactNumber,
    state.couponCode,
    state.details,
    state.email,
    state.confirmEmail,
    state.names,
    state.reservationDate,
    state.reservationTime,
    state.specialRequests,
    state.guideId,
    isPrivate,
    maxPrivateGroupSize,
    minPrivateGroupSize,
    pickUpAnswer,
    pickupLocation,
  ]);

  const [validationErrors] = useMemo(() => validateForm(), [validateForm]);

  const { prices } = product;

  const {
    availabilityMap,
    getTimeSlots,
    availableDatesAsDate,
    isDateAvailable,
    isTimeSlotAvailable,
    getAvailableTimeSlots,
    getGroups,
    getAvailableGroups,
    loading,
    loadedRange,
  } = useAvailability({
    productId: product.id,
    isPrivate,
    duration: { months: MAX_BOOKING_MONTHS_IN_ADVANCE },
    groupSize,
    preloadedAvailability: product.availability,
  });

  const groups = getGroups(state.reservationDate, state.reservationTime);

  const numSeats =
    state.reservationDate !== undefined &&
    state.reservationTime !== undefined &&
    groups
      ? Math.max(
          ...groups
            .filter((group) => group.seatsAvailable >= groupSize)
            .map((group) => group.seatsAvailable),
          0
        )
      : 7;
  useEffect(() => {
    if (
      !loading &&
      state.reservationDate &&
      state.reservationTime &&
      !isTimeSlotAvailable(state.reservationDate, state.reservationTime)
        .available
    ) {
      handleReservationTimeChange(null);
    }
  }, [
    handleReservationTimeChange,
    isTimeSlotAvailable,
    loading,
    state.reservationDate,
    state.reservationTime,
  ]);

  const showWaitingListButton = useFeatureIsOn("checkout-waiting-list-button");

  return (
    <div style={{ background: "#f5f5f5" }}>
      <form
        id="the-checkout-form"
        method="post"
        action="/"
        className={styles.Checkout}
      >
        <div className={styles.FormLeftContainer}>
          <div className={styles.CheckoutTitle}>
            <h2>Enter your tour details</h2>
          </div>

          <div className={styles.AppLayoutPrimary}>
            {product.availabilityType === ProductAvailabilityType.Private && (
              <fieldset className={styles.BookingFormFieldset}>
                <legend>Group price</legend>
                <GroupPriceDetails
                  currency={currency}
                  maximumInGroup={product.maximumInGroup}
                  privatePrices={product.privatePrices}
                />
              </fieldset>
            )}

            <fieldset className={styles.BookingFormFieldset}>
              <legend>
                Number of guests <span style={{ color: "red" }}>*</span>
              </legend>
              <div className={styles.BookingFormFieldsetFields}>
                <GuestPicker
                  value={state.details}
                  onVariantChange={handleVariantChange(
                    !requireAllGuestNames ? 1 : undefined
                  )}
                  prices={prices}
                  currencyCode={currency}
                  maximumInGroup={product.maximumInGroup}
                  email={state.email}
                  availabilityType={product.availabilityType}
                />
              </div>
            </fieldset>
            <fieldset className={styles.BookingFormFieldset}>
              <legend>
                Date & Time <span style={{ color: "red" }}>*</span>
              </legend>
              <div className={styles.BookingFormFieldsetFields}>
                <div>
                  <ReservationDatepicker
                    date={state.reservationDate}
                    time={state.reservationTime}
                    countryName={product.location.country.name}
                    isPrivate={isPrivate}
                    toggleIsPrivate={() =>
                      setIsPrivate((isPrivate) => !isPrivate)
                    }
                    setDate={handleReservationDateChange}
                    setTime={handleReservationTimeChange}
                    inlineCalendar={true}
                    bookingSize={groupSize}
                    getTimeSlots={getTimeSlots}
                    availableDatesAsDate={availableDatesAsDate}
                    isDateAvailable={isDateAvailable}
                    isTimeSlotAvailable={isTimeSlotAvailable}
                    canToggleGroupPrivacy={canToggleGroupPrivacy}
                    getAvailableTimeSlots={getAvailableTimeSlots}
                    getAvailableGroups={getAvailableGroups}
                    minPrivateGroupSize={minPrivateGroupSize}
                    maxPrivateGroupSize={maxPrivateGroupSize}
                    loadedRange={loadedRange}
                    loading={loading}
                  />
                  {showWaitingListButton && (
                    <ProductWaitingListButton
                      productId={product.id}
                      style={{
                        marginTop: "32px",
                        width: "300px",
                      }}
                    />
                  )}
                  {isSumoPracticeProductId(product.id) ? (
                    <p className={styles.SumoAlternateMsg}>
                      <b>
                        Can&apos;t find available spots? <br />
                        We have more Sumo Tours
                      </b>
                      <br />
                      <Link href="/sumo-japan">Find out more info here</Link>
                    </p>
                  ) : null}
                </div>
              </div>
            </fieldset>
            <fieldset className={styles.BookingFormFieldset}>
              <div className={styles.BookingFormFieldsetFields}>
                <ReservationOffer
                  productId={product.id}
                  lineItems={state.details.map((d) => ({
                    priceTypeId: d.typeId,
                    price: d.price,
                    customerType: d.customerType,
                    quantity: d.quantity,
                  }))}
                  currencyCode={currency}
                  couponCode={state.couponCode}
                  coupon={state.coupon}
                  privatePrice={privatePrice}
                />
              </div>
            </fieldset>
            {state.reservationDate && state.reservationTime && (
              <GuidePickerController
                productId={product.id}
                location={product.location}
                date={state.reservationDate}
                time={state.reservationTime}
                availabilityMap={availabilityMap}
                groupSize={groupSize}
                isPrivate={isPrivate}
                value={state.guideId}
                onChange={handleGuidePick}
              />
            )}
            <fieldset className={styles.BookingFormFieldset}>
              <legend>Contact Info</legend>
              <div className={styles.BookingFormFieldsetFields}>
                <ContactPicker
                  onChange={updateForm}
                  validationErrors={validationErrors}
                  state={state}
                  numContacts={requireAllGuestNames ? state.names.length : 1}
                />

                <MarketingSubscriptionCheckbox
                  checked={state.acceptsMarketing}
                  onChange={handleMarketingSubscriptionCheckboxChange}
                />
              </div>
            </fieldset>
            {state.hasEarlyBirdCoupon ? null : (
              <fieldset className={styles.BookingFormFieldset}>
                <legend>Coupon Code</legend>
                <div className={styles.BookingFormFieldsetFields}>
                  <CouponCodePicker
                    productId={product.id}
                    couponCode={state.couponCode}
                    couponApplied={updateCouponCode}
                    currency={currency}
                  />
                </div>
              </fieldset>
            )}
            {product.hasAccomodationVehiclePickup && (
              <fieldset className={styles.BookingFormFieldset}>
                <legend>Do you know where you want to be picked up?</legend>
                <div className={styles.BookingFormFieldsetFields}>
                  <PickupLocation
                    pickupLocationZone={product.pickupLocationZone}
                    setPickupLocation={setPickupLocation}
                    pickupLocation={pickupLocation}
                    defaultMeetingPointQuery={
                      product.meetingPlaceGoogleMapsQuery
                    }
                    defaultMeetingPlaceName={
                      product.translation.meetingPlaceName
                    }
                    hasDefaultVehiclePickup={product.hasDefaultVehiclePickup}
                    hasAccomodationVehiclePickup={
                      product.hasAccomodationVehiclePickup
                    }
                    pickUpAnswer={pickUpAnswer}
                    setPickUpAnswer={setPickUpAnswer}
                  />
                </div>
              </fieldset>
            )}

            {product.hasDefaultVehiclePickup &&
              !product.hasAccomodationVehiclePickup && (
                <fieldset className={styles.BookingFormFieldset}>
                  <legend>Pickup Location</legend>
                  <div className={styles.BookingFormFieldsetFields}>
                    The pickup location will be at the meeting point, please
                    ensure that you arrive on time. The meeting point map link
                    can be found in your confirmation email.
                  </div>
                </fieldset>
              )}
            {product.dietaryRestrictions.length > 0 && (
              <CollapsibleFieldset label="Dietary Info">
                <DietarySummaryCard
                  tableHeaderBackgroundColor="#757575"
                  dietaryRestrictions={product.dietaryRestrictions}
                  titleColor="black"
                  titleSize={16}
                />
              </CollapsibleFieldset>
            )}

            <fieldset className={styles.BookingFormFieldset}>
              <legend>Your Note</legend>
              <div className={styles.BookingFormFieldsetFields}>
                <Input
                  name="special-requests"
                  multiline
                  value={state.specialRequests || ""}
                  onChange={(e) =>
                    updateForm({ specialRequests: e.target.value })
                  }
                  placeholder="Is there anything we should know about you beforehand? Please understand we can only accept requests made well before the tour date."
                />
                <p className={styles.IfWantToBeInSameGroupText}>
                  If you&apos;d like to be in the same tour group as someone
                  else, please mention it here. We recommend making one booking
                  together, as we can&apos;t always guarantee group placement.
                </p>
              </div>
            </fieldset>
            <h2>Booking Summary</h2>
            <AlternateCheckoutSummary
              productId={product.id}
              productPrices={product.prices}
              privatePrice={privatePrice}
              state={state}
              currencyCode={currency}
              numSeats={numSeats}
              setInclusionModalOpen={setInclusionModalOpen}
              inclusionModalOpen={inclusionModalOpen}
              productName={product.translation.name}
              availabilityType={product.availabilityType}
            />
            <p style={{ textAlign: "center", marginBottom: 10 }}>
              Your booking is not yet complete. <br />
              Please proceed to the next page to finalize your payment.
            </p>
            {validationErrors &&
            Object.values(validationErrors.invalidArgs).length > 0
              ? Object.values(validationErrors.invalidArgs).map((i, index) => {
                  return (
                    <p key={index} className={styles.invalidFields}>
                      *{i}
                    </p>
                  );
                })
              : null}
            <div className={styles.CheckoutFormSubmitBox}>
              <CheckoutSubmitButton
                productId={product.id}
                disabled={!!validationErrors}
                formInput={{
                  reservationDate: state.reservationDate,
                  reservationTime: state.reservationTime,
                  details: state.details,
                  guideId: state.guideId,
                  specialRequests: state.specialRequests,
                  couponCode: state.couponCode,

                  names: state.names,
                  email: state.email,
                  confirmEmail: state.confirmEmail,
                  contactNumber: state.contactNumber,

                  acceptsMarketing: state.acceptsMarketing,
                  isPrivate: isPrivate,
                  maxPrivateGroupSize,
                  minPrivateGroupSize,
                }}
                currencyCode={currency}
                languageCode={language}
                pickupAnswer={pickUpAnswer}
                pickupLocation={pickupLocation}
              />

              {product.cancellationLimit && (
                <CancellationPolicyIncentive
                  limitInHours={product.cancellationLimit}
                />
              )}
            </div>
            <SecurePaymentsFooter />
            <div className={styles.HowDisWorksLink} onClick={onOpenDisModal}>
              <BiInfoCircle
                color="#D81B60"
                size={22}
                style={{ position: "relative", top: 6 }}
              />{" "}
              <span style={{ color: "#D81B60", textDecoration: "underline" }}>
                How it works
              </span>
            </div>
          </div>
          <CheckoutFooter />
        </div>

        <div className={styles.AppLayoutSecondaryCheckout}>
          <div className={styles.BookingFormSticky}>
            <ProductPreview
              name={product.translation.name}
              poster={product.poster}
              location={product.location.translation}
              reviews={product.reviews}
            />
            <aside className={styles.CheckoutSummary}>
              <h1>Booking Summary</h1>
              <CheckoutSummary
                productId={product.id}
                productPrices={product.prices}
                state={state}
                currencyCode={currency}
                numSeats={numSeats}
                privatePrice={privatePrice}
                availabilityType={product.availabilityType}
              />
            </aside>
            <div className={styles.RSideInclusions}>
              {product.inclusions?.length > 0 && (
                <Inclusions
                  inclusions={product.inclusions}
                  isModalOpen={inclusionModalOpen}
                />
              )}
            </div>
            <p className={styles.FaqLink}>
              Have any more questions?{" "}
              <span onClick={onOpenFaqModal}>Read FAQ</span>
            </p>
          </div>
        </div>
      </form>
      <Modal
        open={openFaq}
        onClose={onCloseFaqModal}
        ref={modalRef}
        initialFocusRef={modalRef}
        blockScroll={false}
        classNames={{
          modal: styles.CustomModal,
        }}
      >
        {product?.faqs && product.faqs.length > 0 && (
          <FAQsSection faqs={product.faqs ?? []} />
        )}
      </Modal>

      <Modal
        open={openDis}
        blockScroll={false}
        onClose={onCloseDisModal}
        ref={modalRef}
        initialFocusRef={modalRef}
        classNames={{
          modal: styles.CustomModal2,
        }}
      >
        <HowThisWorks />
      </Modal>
    </div>
  );
}
